import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import InternshipApplication from '../../student/InternshipApplication/InternshipApplication';
import { companiesData } from '../../../data/mock/companies';
import { 
  CompanyCard, 
  CompanyHeader, 
  CompanyLogo, 
  CompanyInfo, 
  CompanyName, 
  CompanyIndustry, 
  CompanySize, 
  CompanyBody, 
  CompanyDescription, 
  InterestTagsContainer, 
  InterestTag, 
  CompanyFooter, 
  MatchPercentBadge 
} from '../../../styles/components/CompanyCard';
import { 
  NotificationBar 
} from '../../../styles/components/Notification';
import { 
  ModalOverlay as CompanyDetailsModal, 
  ModalContent, 
  CloseButton, 
  ModalHeader, 
  ModalSection, 
  SectionTitle, 
  ActionButtons 
} from '../../../styles/components/Modal';

const SuggestedCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filterIndustry, setFilterIndustry] = useState('');
  const [matchFilter, setMatchFilter] = useState('all');
  const [savedCompanies, setSavedCompanies] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setCompanies(companiesData);
      setLoading(false);
    }, 800);
  }, []);

  // Filter companies based on industry and match percentage
  const filteredCompanies = companies.filter(company => {
    const industryMatch = filterIndustry === '' || company.industry === filterIndustry;
    const matchPercentMatch = 
      matchFilter === 'all' || 
      (matchFilter === 'high' && company.match >= 90) ||
      (matchFilter === 'medium' && company.match >= 80 && company.match < 90) ||
      (matchFilter === 'low' && company.match < 80);
    
    return industryMatch && matchPercentMatch;
  });

  // Get unique industries
  const industries = [...new Set(companies.map(company => company.industry))];

  // View company details
  const handleViewCompany = (company) => {
    setSelectedCompany(company);
  };

  // Close company details modal
  const closeCompanyDetails = () => {
    setSelectedCompany(null);
  };

  // Show notification
  const showNotificationMessage = (message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  // Handle saving a company to favorites
  const handleSaveCompany = (company) => {
    const isSaved = savedCompanies.some(saved => saved.id === company.id);
    
    if (!isSaved) {
      setSavedCompanies([...savedCompanies, company]);
      showNotificationMessage(`${company.name} has been saved to your favorites`);
    } else {
      setSavedCompanies(savedCompanies.filter(saved => saved.id !== company.id));
      showNotificationMessage(`${company.name} has been removed from your favorites`, 'info');
    }
  };

  // Handle applying to an internship
  const handleApplyToInternship = (company) => {
    setShowApplicationModal(true);
  };

  // Close application modal
  const closeApplicationModal = () => {
    setShowApplicationModal(false);
    closeCompanyDetails();
  };

  // Check if a company is saved
  const isCompanySaved = (companyId) => {
    return savedCompanies.some(company => company.id === companyId);
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <h1>Suggested Companies</h1>
          <p>Based on your profile, skills, and career interests</p>
        </HeaderContent>
      </PageHeader>

      <FilterSection>
        <FilterCard>
          <FilterHeader>
            <h3>Filter Companies</h3>
            {(filterIndustry !== '' || matchFilter !== 'all') && (
              <ClearButton onClick={() => {
                setFilterIndustry('');
                setMatchFilter('all');
              }}>
                Clear Filters
              </ClearButton>
            )}
          </FilterHeader>
          
          <FilterGroup>
            <FilterLabel>Industry</FilterLabel>
            <FilterSelect 
              value={filterIndustry} 
              onChange={(e) => setFilterIndustry(e.target.value)}
            >
              <option value="">All Industries</option>
              {industries.map((industry, index) => (
                <option key={index} value={industry}>{industry}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Match Percentage</FilterLabel>
            <FilterSelect 
              value={matchFilter} 
              onChange={(e) => setMatchFilter(e.target.value)}
            >
              <option value="all">All Matches</option>
              <option value="high">High Match (90%+)</option>
              <option value="medium">Medium Match (80-89%)</option>
              <option value="low">Low Match (Below 80%)</option>
            </FilterSelect>
          </FilterGroup>
        </FilterCard>
      </FilterSection>

      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading suggested companies...</p>
        </LoadingContainer>
      ) : (
        <CompaniesContainer>
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map(company => (
              <CompanyCard key={company.id} matchPercentage={company.match}>
                <CompanyHeader>
                  <CompanyLogo src={company.logo} alt={`${company.name} logo`} />
                  <CompanyInfo>
                    <CompanyName>{company.name}</CompanyName>
                    <CompanyIndustry>{company.industry}</CompanyIndustry>
                    <CompanySize>
                      {company.size} • {company.interests && company.interests.length} matching interests
                    </CompanySize>
                  </CompanyInfo>
                  <MatchPercentBadge matchPercentage={company.match}>
                    {company.match}% Match
                  </MatchPercentBadge>
                </CompanyHeader>
                
                <CompanyBody>
                  <CompanyDescription>{company.description}</CompanyDescription>
                  
                  <InterestTagsContainer>
                    {company.interests && company.interests.slice(0, 3).map((interest, index) => (
                      <InterestTag key={index}>{interest}</InterestTag>
                    ))}
                  </InterestTagsContainer>
                </CompanyBody>
                
                <CompanyFooter>
                  <ViewDetailsButton onClick={() => handleViewCompany(company)}>
                    View Details
                  </ViewDetailsButton>
                  <SaveButton 
                    onClick={() => handleSaveCompany(company)}
                    saved={isCompanySaved(company.id)}
                  >
                    <SaveIcon>{isCompanySaved(company.id) ? '♥' : '♡'}</SaveIcon> 
                    {isCompanySaved(company.id) ? 'Saved' : 'Save'}
                  </SaveButton>
                </CompanyFooter>
              </CompanyCard>
            ))
          ) : (
            <NoResultsContainer>
              <NoResultsIcon>🔍</NoResultsIcon>
              <h3>No companies match your filter criteria</h3>
              <p>Try adjusting your filters or view all available companies</p>
              <ResetButton onClick={() => {
                setFilterIndustry('');
                setMatchFilter('all');
              }}>
                Reset Filters
              </ResetButton>
            </NoResultsContainer>
          )}
        </CompaniesContainer>
      )}

      {selectedCompany && !showApplicationModal && (
        <CompanyDetailsModal>
          <ModalContent>
            <CloseButton onClick={closeCompanyDetails}>&times;</CloseButton>
            
            <ModalHeader>
              <CompanyLogoLarge src={selectedCompany.logo} alt={`${selectedCompany.name} logo`} />
              <ModalCompanyInfo>
                <h2>{selectedCompany.name}</h2>
                <DetailsContainer>
                  <DetailItem>
                    <DetailLabel>Industry:</DetailLabel>
                    <DetailValue>{selectedCompany.industry}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Size:</DetailLabel>
                    <DetailValue>{selectedCompany.size}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Match:</DetailLabel>
                    <DetailValue>
                      <MatchPercentBadge matchPercentage={selectedCompany.match}>
                        {selectedCompany.match}%
                      </MatchPercentBadge>
                    </DetailValue>
                  </DetailItem>
                </DetailsContainer>
              </ModalCompanyInfo>
            </ModalHeader>
            
            <ModalSection>
              <SectionTitle>About</SectionTitle>
              <p>{selectedCompany.description}</p>
            </ModalSection>
            
            <ModalSection>
              <SectionTitle>Areas of Interest</SectionTitle>
              <InterestTagsContainer>
                {selectedCompany.interests && selectedCompany.interests.map((interest, index) => (
                  <InterestTag key={index}>{interest}</InterestTag>
                ))}
              </InterestTagsContainer>
              {(!selectedCompany.interests || selectedCompany.interests.length === 0) && (
                <p>No specific areas of interest listed for this company.</p>
              )}
            </ModalSection>
            
            <ModalSection>
              <SectionTitle>Student Recommendations</SectionTitle>
              {selectedCompany.recommendations && selectedCompany.recommendations.map((rec, index) => (
                <RecommendationItem key={index}>
                  <RecommendationHeader>
                    <RecommendationName>{rec.studentName}</RecommendationName>
                    <RatingContainer>
                      {Array(5).fill().map((_, i) => (
                        <Star key={i} filled={i < Math.floor(rec.rating)}>★</Star>
                      ))}
                      <RatingText>{rec.rating.toFixed(1)}</RatingText>
                    </RatingContainer>
                  </RecommendationHeader>
                  <RecommendationText>"{rec.comment}"</RecommendationText>
                </RecommendationItem>
              ))}
              {(!selectedCompany.recommendations || selectedCompany.recommendations.length === 0) && (
                <p>No student recommendations available for this company yet.</p>
              )}
            </ModalSection>
            
            <ActionButtons>
              <PrimaryButton onClick={() => handleApplyToInternship(selectedCompany)}>
                Apply for Internship
              </PrimaryButton>
              <SecondaryButton 
                onClick={() => handleSaveCompany(selectedCompany)}
                saved={isCompanySaved(selectedCompany.id)}
              >
                {isCompanySaved(selectedCompany.id) ? 'Remove from Favorites' : 'Save to Favorites'}
              </SecondaryButton>
            </ActionButtons>
          </ModalContent>
        </CompanyDetailsModal>
      )}
      
      {/* Application Modal */}
      {showApplicationModal && selectedCompany && (
        <CompanyDetailsModal>
          <ModalContent style={{ maxWidth: '850px', maxHeight: '90vh', overflow: 'auto' }}>
            <InternshipApplication 
              internship={{
                title: `${selectedCompany.industry} Intern`,
                company: selectedCompany.name,
                logo: selectedCompany.logo,
                location: selectedCompany.location || 'Not specified',
                paid: true,
                salary: 'Competitive'
              }}
              onClose={closeApplicationModal}
              onSubmit={() => {
                closeApplicationModal();
                showNotificationMessage(`Application submitted to ${selectedCompany.name}`);
              }}
            />
          </ModalContent>
        </CompanyDetailsModal>
      )}
      
      {showNotification && (
        <NotificationBar type={notificationType}>
          {notificationMessage}
        </NotificationBar>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PageHeader = styled.div`
  background-color: #f7f7f7;
  padding: 20px;
  border-bottom: 1px solid #ddd;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const FilterSection = styled.div`
  margin-top: 20px;
`;

const FilterCard = styled.div`
  background-color: #fff;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ClearButton = styled.button`
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.3);
  }
`;

const FilterGroup = styled.div`
  margin-bottom: 20px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: bold;
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const LoadingSpinner = styled.div`
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const CompaniesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ViewDetailsButton = styled.button`
  background-color: rgba(33, 150, 243, 0.85);
  color: white;
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(33, 150, 243, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
  }
`;

const SaveButton = styled.button`
  background-color: ${props => props.saved ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  color: #2196f3;
  border: 1px solid rgba(33, 150, 243, 0.5);
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.1);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(33, 150, 243, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(227, 242, 253, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
  }
`;

const SaveIcon = styled.span`
  margin-right: 4px;
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const NoResultsIcon = styled.span`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ResetButton = styled.button`
  background-color: rgba(33, 150, 243, 0.85);
  color: white;
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(33, 150, 243, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
  }
`;

const ModalCompanyInfo = styled.div`
  flex: 1;
  margin-left: 20px;
`;

const RecommendationItem = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const RecommendationName = styled.span`
  font-weight: bold;
  color: #333;
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const RatingText = styled.span`
  font-weight: bold;
  color: #ffc107;
  margin-left: 4px;
`;

const RecommendationText = styled.p`
  font-style: italic;
  color: #666;
  margin: 0;
`;

const CompanyLogoLarge = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #666;
  width: 80px;
`;

const DetailValue = styled.span`
  color: #333;
`;

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#ffc107' : '#e0e0e0'};
  margin-right: 2px;
`;

const PrimaryButton = styled.button`
  background-color: rgba(66, 133, 244, 0.85);
  color: white;
  border: 1px solid rgba(66, 133, 244, 0.5);
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(66, 133, 244, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(66, 133, 244, 0.3);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(66, 133, 244, 0); }
    100% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0); }
  }
`;

const SecondaryButton = styled.button`
  background-color: ${props => props.saved ? 'rgba(232, 240, 254, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  color: #4285F4;
  border: 1px solid rgba(66, 133, 244, 0.5);
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.1);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(66, 133, 244, 0.1), transparent);
    transform: translateX(-100%);
    transition: 0.6s;
  }
  
  &:hover {
    background-color: rgba(232, 240, 254, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.2);
    
    &:before {
      transform: translateX(100%);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.3);
  }
  }
`;

export default SuggestedCompanies;